import MapboxDraw from '@mapbox/mapbox-gl-draw'
import bbox from '@turf/bbox'
import mapbox, { FullscreenControl, Map, NavigationControl } from 'mapbox-gl'

import config from '@/components/config'
import { mapBorderColors, mapFillColors, mapboxLayerTypes, mapboxSourceTypes } from '@/components/mixins/constants'

/**
 * Coordinate information about a point
 * @typedef {Object} Point
 * @property {number} lat - latitude coordinate
 * @property {number} lng - longitude coordinate
 */

/**
 * Style attributes for boxes
 * @typedef {Object} Style
 * @property {string} opacity - opacity of box fill
 * @property {string} [fillColor] - color to fill area with
 * @property {string} borderColor - color of border
 * @property {string} borderWidth - width of border
 */

/**
 * Box to be drawn on map
 * @typedef {Object} Box
 * @property {string} id = unique identifier of box
 * @property {string} [imageUrl] - url of image to be shown in box as background
 * @property {Point[]} featurePolygon - coordinates of the boxes' points
 * @property {Style} style - style attributes for box
 * @property {string[]} containerUuids - container data to show in box
 */

/**
 * Fit bound to fit mapbox map to
 * @typedef {Object} FitBounds
 * @property {number[]} sw - bottom left (south west) coordinate to fit map to
 * @property {number[]} ne - top right (north east) coordinate to fit map to
 */

/**
 * Mapbox feature collection
 * @typedef {Object} FeatureCollection
 * @property {MapboxFeature[]} features - mapbox features
 */

/**
 * Mapbox features collection features
 * @typedef {Object} MapboxFeature
 * @property {MapboxGeometry} geometry - mapbox geometry object
 */

/**
 * Mapbox geometry
 * @typedef {Object} MapboxGeometry
 * @property {number[][]} coordinates - mapbox coordinates
 */

/**
 * Custom options to init a mapbox
 * @typedef {Object} InitMapOptions
 * @property {string} [container=map] - element to add map to
 * @property {Point} [center] - coordinates where to center the map
 * @property {number} [zoom=config.MAP_DEFAULT_ZOOM] - zoom level to start the map with
 * @property {boolean} [enableFullscreen] - enable map to be viewed in fullscreen
 * @property {boolean} [enableNavigationControls] - enable extra navigation control buttons on the map
 * @property {mapbox.Control[]} [controls] - additional custom controls to add to the map
 * @property {Box[]} [boxes] - boxes to be drawn on map
 * @property {FitBounds} [fitBounds] - bounds to fit zoom to on map init
 */

/**
 * Options to init custom control button
 * @typedef {Object} InitCustomControlOptions
 * @property {function} clickFunction - function to execute on click
 * @property {string} materialIcon - name of material icon
 * @property {('top-left'|'top-right'|'bottom-left'|'bottom-right')} [position] - allowed position to place control on the map at
 */

const mapUtils = {
  /**
   * Inits mapbox map
   * @param {InitMapOptions} options - options for map initialization
   * @returns mapbox.Map
   */
  initMap: ({ center, container, zoom, enableFullscreen, enableNavigationControls, controls, boxes, fitBounds }) => {
    mapbox.accessToken = config.MAPBOX_TOKEN

    const map = new Map({
      container: container ?? 'map',
      style: 'mapbox://styles/mapbox/streets-v11',
      center: {
        lat: center?.lat ?? config.MAP_DEFAULT_CENTER.lat,
        lng: center?.lng ?? config.MAP_DEFAULT_CENTER.lng,
      },
      zoom: zoom ?? config.MAP_DEFAULT_ZOOM,
    })

    controls?.forEach(control => map.addControl(control))

    if (enableFullscreen) {
      map.addControl(new FullscreenControl())
    }

    if (enableNavigationControls) {
      map.addControl(new NavigationControl())
    }

    map.on('load', () => {
      if (boxes?.length > 0) {
        // Add boxes to map
        for (const box of boxes) {
          box.sourceName = `boxSource:${box.id}`
          addBoxToMap(box, map)
        }
      }
      if (fitBounds && fitBounds.features.length > 0) {
        // Calculate bounding box of coordinates and fit map to it
        const boundingBox = bbox(fitBounds)
        window.setTimeout(() => {
          map.fitBounds(boundingBox, {
            padding: config.MAPBOX_FIT_BOUNDS_PADDING,
          })
        }, config.MAPBOX_ZOOM_AFTER_M_SECONDS)
      }
    })

    return map
  },
  initPolygonDraw: ({ polygon, trash }) => {
    return new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon,
        trash,
      },
    })
  },

  /**
   * Extract coordinates from mapbox feature collection as Point objects
   * @param {FeatureCollection} featureCollection
   * @returns {Point[]} coordinates
   */
  mbFeatureCollectionToLatLngCoordinates: featureCollection => {
    return featureCollection.features[0].geometry.coordinates[0].map(pairOfCoordinates => {
      return {
        lng: pairOfCoordinates[0],
        lat: pairOfCoordinates[1],
      }
    })
  },
  /**
   * Calculates center point of coordinates
   * @param {Point[]} coordinates
   * @returns {Point} center coordinates
   */
  calculateCenterOfCoordinates: coordinates => {
    return {
      lat:
        coordinates.map(pairOfCoordinates => pairOfCoordinates.lat).reduce((acc, current) => acc + current) /
        coordinates.length,
      lng:
        coordinates.map(pairOfCoordinates => pairOfCoordinates.lng).reduce((acc, current) => acc + current) /
        coordinates.length,
    }
  },
  /**
   * Creates custom control
   * @param {InitCustomControlOptions} options - options for custom control creation
   * @returns mapbox.Control
   */
  initCustomControl: ({ materialIcon, clickFunction, position }) => {
    return {
      onAdd: () => {
        const rotateButton = document.createElement('button')
        rotateButton.setAttribute('type', 'button')
        rotateButton.setAttribute('class', 'button button-green material-icons button-round')
        rotateButton.setAttribute('style', 'z-index:999;pointer-events:auto;margin-left:12px;margin-top:12px;')
        rotateButton.appendChild(document.createTextNode(materialIcon))
        rotateButton.addEventListener('click', clickFunction)
        return rotateButton
      },
      getDefaultPosition: () => {
        return position ?? 'top-left'
      },
    }
  },
  /**
   * Sorts coordinates clockwise
   * @param {Point[]} coordinates - coordinates to sort clockwise
   * @returns number[][]
   */
  sortCoordinatesClockwise: coordinates => {
    const center = mapUtils.calculateCenterOfCoordinates(coordinates)

    const coordinatesWithAngle = coordinates.map(pairOfCoordinates => {
      return {
        ...pairOfCoordinates,
        centerAngle:
          (Math.atan2(pairOfCoordinates.lat - center.lat, pairOfCoordinates.lng - center.lng) * 180) / Math.PI,
      }
    })

    const sortedCoordinates = coordinatesWithAngle.sort((a, b) => (a.centerAngle < b.centerAngle ? 1 : -1))

    return [...sortedCoordinates.map(pairOfCoordinates => [pairOfCoordinates.lng, pairOfCoordinates.lat])]
  },
  coordinatePairArrayToLngLatObject: coordinatePairs =>
    coordinatePairs.map(pairOfCoordinates => ({
      lng: pairOfCoordinates[0],
      lat: pairOfCoordinates[1],
    })),
  siteToBox: site => {
    const imgUrl = site.image ? site.image.url : null

    const parsedPolygon = JSON.parse(site.mb_feature_polygon)

    return {
      id: `site:${site.uuid}`,
      imageUrl: imgUrl,
      imageCoordinates: parsedPolygon.features[0].geometry.coordinates[0],
      featurePolygon: parsedPolygon,
      style: {
        opacity: 0.6,
        borderColor: mapFillColors.BLUE,
        borderWidth: 2,
      },
    }
  },
  areaToBox: area => ({
    id: `area:${area.uuid}`,
    featurePolygon: JSON.parse(area.mb_feature_polygon),
    style: {
      fillColor: mapFillColors.GREEN,
      opacity: 0.5,
      borderColor: mapBorderColors.BLACK,
      borderWidth: 1,
    },
  }),
  zoneToBox: zone => {
    const jsonBox = mapUtils.areaToBox(zone)
    jsonBox.style = {
      ...jsonBox.style,
      fillColor: mapFillColors.RED,
      opacity: 0.4,
    }
    return jsonBox
  },
  createCustomMarkerFromMaterialIcons: (materialIconName, fontSize) => {
    const el = document.createElement('div')
    el.innerHTML = `
      <span class="material-icons" style="font-size: ${fontSize}">${materialIconName}</span>
    `
    return el
  },
  createCustomMarkerWithLabel: (color, label) => {
    const el = document.createElement('div')

    el.innerHTML = `
      <svg display="block" height="41px" width="27px" viewBox="0 0 27 41">
        <!-- Default defs -->
        <defs>
          <radialGradient id="shadowGradient">
            <stop offset="10%" stop-opacity="0.4"></stop>
            <stop offset="100%" stop-opacity="0.05"></stop>
          </radialGradient>
        </defs>

        <!-- Default ellipse -->
        <ellipse cx="13.5" cy="34.8" rx="10.5" ry="5.25" fill="url(#shadowGradient)">
        </ellipse>

        <!-- Default path with a custom color (#1C9600 --> color) -->
        <path fill="${color}" d="M27,13.5C27,19.07 20.25,27 14.75,34.5C14.02,35.5 12.98,35.5 12.25,34.5C6.75,27 0,19.22 0,13.5C0,6.04 6.04,0 13.5,0C20.96,0 27,6.04 27,13.5Z"></path><path opacity="0.25" d="M13.5,0C6.04,0 0,6.04 0,13.5C0,19.22 6.75,27 12.25,34.5C13,35.52 14.02,35.5 14.75,34.5C20.25,27 27,19.07 27,13.5C27,6.04 20.96,0 13.5,0ZM13.5,1C20.42,1 26,6.58 26,13.5C26,15.9 24.5,19.18 22.22,22.74C19.95,26.3 16.71,30.14 13.94,33.91C13.74,34.18 13.61,34.32 13.5,34.44C13.39,34.32 13.26,34.18 13.06,33.91C10.28,30.13 7.41,26.31 5.02,22.77C2.62,19.23 1,15.95 1,13.5C1,6.58 6.58,1 13.5,1Z"></path>

        <!-- Default circle with a bigger radius (5.5 --> 9) -->
        <circle fill="white" cx="13.5" cy="13.5" r="9"></circle>

        <!-- This is from us -->
        <text
            x="13.5"
            y="14.0"
            text-anchor="middle"
            dominant-baseline="middle"
            font-family="Arial, sans-serif"
            font-size="10"
            font-weight="bold"
            fill="#222">
            ${label}
        </text>
      </svg>
    `
    return el
  },
}

const addBoxToMap = (box, map) => {
  addBaseBoxToMap(box, map)

  if (box.imageUrl) {
    // addImageBackgroundToBox(box, map) // to plot image on mapbox if box.imageUrl
    return
  } else {
    fillBoxBackground(box, map)
  }
}

const addBaseBoxToMap = (box, map) => {
  map.addSource(box.sourceName, {
    type: mapboxSourceTypes.GEOJSON,
    data: box.featurePolygon,
  })
  map.addLayer({
    id: `outline:${box.sourceName}`,
    type: mapboxLayerTypes.LINE,
    source: box.sourceName,
    layout: {},
    paint: {
      ...(box.style?.borderColor && {
        'line-color': box.style?.borderColor,
      }),
      ...(box.style?.borderWidth && {
        'line-width': box.style?.borderWidth,
      }),
    },
  })
}

// eslint-disable-next-line no-unused-vars
const addImageBackgroundToBox = (box, map) => {
  const imageSourceName = `imageSource:${box.id}`
  map.addSource(imageSourceName, {
    type: mapboxSourceTypes.IMAGE,
    url: box.imageUrl,
    coordinates: box.imageCoordinates.slice(0, 4),
  })
  map.addLayer({
    id: `imageLayer:${box.id}`,
    type: mapboxLayerTypes.RASTER,
    paint: { 'raster-opacity': box.style.opacity },
    source: imageSourceName,
  })
}

const fillBoxBackground = (box, map) => {
  const boxAreaName = `boxArea:${box.id}`
  map.addLayer({
    id: `fill:${boxAreaName}`,
    type: mapboxLayerTypes.FILL,
    source: box.sourceName,
    layout: {},
    paint: {
      ...(box.style?.fillColor && {
        'fill-color': box.style?.fillColor,
      }),
      ...(box.style?.opacity && {
        'fill-opacity': box.style?.opacity,
      }),
    },
  })
}

export default mapUtils
